import { useSeoMeta } from "@unhead/react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Zap } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { getModule } from "@/lib/curriculum";
import { useProgress } from "@/hooks/useProgress";
import NotFound from "./NotFound";

export default function ModulePage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const module = getModule(moduleId ?? "");
  const { isLessonComplete, moduleCompletion } = useProgress();

  useSeoMeta({
    title: module ? `${module.title} — BCH School` : "BCH School",
    description: module?.tagline,
  });

  if (!module) return <NotFound />;

  const mc = moduleCompletion[module.id];
  const pct = mc?.total ? Math.round((mc.done / mc.total) * 100) : 0;

  return (
    <Layout>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" /> All modules
        </Link>
      </Button>

      <div className="mb-8 flex items-start gap-4">
        <span
          className={cn(
            "flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-gradient-to-br text-4xl shadow-sm",
            module.gradient,
          )}
        >
          {module.emoji}
        </span>
        <div className="flex-1">
          <h1 className="text-3xl font-extrabold tracking-tight">
            {module.title}
          </h1>
          <p className="mt-1 text-muted-foreground">{module.tagline}</p>
          <div className="mt-3 flex items-center gap-3">
            <Progress value={pct} className="h-2 max-w-xs flex-1" />
            <span className="text-sm font-semibold text-muted-foreground">
              {mc?.done ?? 0}/{mc?.total ?? module.lessons.length}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {module.lessons.map((lesson, i) => {
          const done = isLessonComplete(lesson.id);
          return (
            <Link
              key={lesson.id}
              to={`/lesson/${lesson.id}`}
              className="group block focus-visible:outline-none"
            >
              <Card className="border-2 transition-all hover:border-primary/40 hover:shadow-md group-focus-visible:ring-2 group-focus-visible:ring-ring">
                <CardContent className="flex items-center gap-4 p-4">
                  <span className="shrink-0">
                    {done ? (
                      <CheckCircle2 className="h-7 w-7 text-primary" />
                    ) : (
                      <Circle className="h-7 w-7 text-muted-foreground/40" />
                    )}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Lesson {i + 1}
                      </span>
                    </div>
                    <h3 className="font-bold leading-tight">{lesson.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {lesson.summary}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                    <Zap className="h-3 w-3 fill-current" />
                    {lesson.xp}
                  </span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </Layout>
  );
}
